import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const programs = [
  {
    key: 'stamps',
    label: 'Carimbos',
    title: 'Cada visita conta.',
    description:
      'O clássico cartão de carimbos, sem papel. Seu cliente acompanha as visitas e sabe quanto falta para a próxima recompensa.',
    detail: 'Um café, um corte, um motivo para voltar.',
    alt: 'Exemplo de cartão de carimbos Café Aurora no celular',
  },
  {
    key: 'points',
    label: 'Pontos',
    title: 'Boas escolhas merecem pontos.',
    description:
      'Um cartão para acompanhar os pontos do cliente e apresentar as recompensas do seu negócio. Do saldo ao próximo benefício, tudo à mão.',
    detail: 'Recompensas com a cara da sua marca.',
    alt: 'Exemplo de cartão de pontos flora no celular',
  },
  {
    key: 'cashback',
    label: 'Cashback',
    title: 'Cashback para a próxima visita.',
    description:
      'Mostre o saldo de cashback direto na carteira digital. Um jeito simples de lembrar que a próxima compra já tem um benefício esperando.',
    detail: 'O saldo fica no cartão. A vontade é de voltar.',
    alt: 'Exemplo de cartão de cashback forno no celular',
  },
  {
    key: 'members',
    label: 'Membros',
    title: 'Um lugar no seu clube.',
    description:
      'Identifique quem faz parte do seu programa com um cartão de membro. A marca, os dados e os benefícios reunidos na carteira do cliente.',
    detail: 'Para quem já é de casa.',
    alt: 'Exemplo de cartão de membros Corte e Companhia no celular',
  },
  {key:'discount',label:'Desconto',title:'Um benefício que acompanha.',description:'Um cartão para apresentar a condição especial do seu programa e identificar quem pode aproveitar.',detail:'Formato demonstrativo. Emissão ainda não disponível.',alt:'Conceito de cartão de desconto linho no celular'},
  {key:'coupon',label:'Cupom',title:'O convite para a primeira visita.',description:'Uma oferta em um cartão fácil de guardar. Um formato para apresentar uma promoção e suas condições.',detail:'Formato demonstrativo. Emissão ainda não disponível.',alt:'Conceito de cartão de cupom miga no celular'},
  {key:'gift',label:'Vale-presente',title:'Um carinho para escolher.',description:'A experiência do seu negócio em forma de presente. Um cartão para apresentar o valor e quem vai receber.',detail:'Formato demonstrativo. Emissão ainda não disponível.',alt:'Conceito de vale-presente flora no celular'},
];

export function CardPrograms() {
  const [selected, setSelected] = useState(0);
  const railRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<number | null>(null);
  const selectionRef = useRef(0);
  function position(index: number) {
    const panels =
      railRef.current?.querySelectorAll<HTMLElement>('.program-panel');
    const offset = panels?.[index] ? panels[index].offsetLeft - panels[0].offsetLeft : 0;
    const rail = railRef.current;
    return rail && rail.clientWidth > 0
      ? Math.min(offset, Math.max(0, rail.scrollWidth - rail.clientWidth))
      : offset;
  }
  function select(index: number, instant = false) {
    const next = Math.max(0, Math.min(programs.length - 1, index));
    setSelected(next);
    selectionRef.current = next;
    targetRef.current = next;
    railRef.current?.scrollTo?.({
      left: position(next),
      behavior:
        instant ||
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
          ? 'instant'
          : 'smooth',
    });
  }
  function syncScroll() {
    const rail = railRef.current;
    if (!rail) return;
    if (targetRef.current !== null) {
      if (Math.abs(rail.scrollLeft - position(targetRef.current)) < 3)
        targetRef.current = null;
      else return;
    }
    const nearest = programs.reduce(
      (best, _, index) =>
        Math.abs(position(index) - rail.scrollLeft) <
        Math.abs(position(best) - rail.scrollLeft)
          ? index
          : best,
      0,
    );
    setSelected(nearest);
    selectionRef.current = nearest;
  }
  useEffect(() => {
    const align = () => select(selectionRef.current, true);
    window.addEventListener('resize', align);
    return () => window.removeEventListener('resize', align);
  }, []);
  return (
    <section className="program-showcase" id="modelos" aria-labelledby="programs-title">
      <div className="program-heading">
        <div>
          <h3 id="programs-title">Um cartão para cada jeito de fidelizar.</h3>
        </div>
        <div
          className="program-tabs"
          role="tablist"
          aria-label="Modelos de cartões"
          onKeyDown={(event) => {
            const direction =
              event.key === 'ArrowRight'
                ? 1
                : event.key === 'ArrowLeft'
                  ? -1
                  : 0;
            if (!direction && event.key !== 'Home' && event.key !== 'End')
              return;
            event.preventDefault();
            const next =
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? programs.length - 1
                  : (selected + direction + programs.length) % programs.length;
            select(next);
            event.currentTarget
              .querySelectorAll<HTMLButtonElement>('[role="tab"]')
              [next]?.focus();
          }}
        >
          {programs.map((program, index) => (
            <button
              key={program.key}
              type="button"
              role="tab"
              id={`program-tab-${program.key}`}
              aria-controls={`program-panel-${program.key}`}
              aria-selected={selected === index}
              tabIndex={selected === index ? 0 : -1}
              onClick={() => select(index)}
            >
              {program.label}
            </button>
          ))}
        </div>
        <div className="gallery-controls program-controls">
          <button type="button" aria-label="Modelo anterior" disabled={selected === 0} onClick={() => select(selected - 1)}><ArrowLeft size={20} aria-hidden="true" /></button>
          <button type="button" aria-label="Próximo modelo" disabled={selected === programs.length - 1} onClick={() => select(selected + 1)}><ArrowRight size={20} aria-hidden="true" /></button>
        </div>
      </div>
      <div
        className="program-rail"
        ref={railRef}
        data-testid="program-rail"
        onScroll={syncScroll}
        onTouchStart={() => {
          targetRef.current = null;
        }}
        onWheel={() => {
          targetRef.current = null;
        }}
      >
        {programs.map((program, index) => (
          <article
            className="program-panel"
            role="tabpanel"
            id={`program-panel-${program.key}`}
            aria-labelledby={`program-tab-${program.key}`}
            aria-hidden={selected !== index}
            inert={selected !== index}
            tabIndex={selected === index ? 0 : -1}
            key={program.key}
          >
            <div className="program-copy">
              <span className="program-count">
                0{index + 1} / {program.label}
              </span>
              <h4>{program.title}</h4>
              <p>{program.description}</p>
              <span className="program-detail">{program.detail}</span>
            </div>
            <div className="program-art">
              <img
                src={`/images/program-${program.key}.avif`}
                alt={program.alt}
                width="1024"
                height="1536"
                loading="lazy"
              />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
